import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUpcomingPrograms } from '../api'
import { ROLE_DISPLAY_NAME } from '../../../pages/Login/api'

interface ProgramItem {
  id: number
  name: string
  date: string
  end_date?: string
  location?: string
  mode?: string
  status?: string
  fees?: string
  seats?: number
  participants?: number
  image?: string
  thumb?: string
  booking_end_date?: string
  role_id?: number[]
}

const UpcomingProgramList: React.FC<{
  perPage?: number
  setPerPage?: (val: number) => void
}> = () => {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useUpcomingPrograms()
  const programs: ProgramItem[] = data?.data || []
  const trackRef = useRef<HTMLDivElement | null>(null)

  const scrollLeft = () => {
    const track = trackRef.current
    if (!track) return
    const cardWidth =
      track.querySelector<HTMLElement>('[data-card]')?.offsetWidth || 300
    track.scrollBy({ left: -(cardWidth + 16), behavior: 'smooth' })
  }

  const scrollRight = () => {
    const track = trackRef.current
    if (!track) return
    const cardWidth =
      track.querySelector<HTMLElement>('[data-card]')?.offsetWidth || 300
    track.scrollBy({ left: cardWidth + 16, behavior: 'smooth' })
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  const isBookingOpen = (bookingEndDate?: string) => {
    if (!bookingEndDate) return true
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const endDate = new Date(bookingEndDate)
    endDate.setHours(0, 0, 0, 0)
    return endDate >= today
  }

  const getDaysLeft = (bookingEndDate?: string) => {
    if (!bookingEndDate) return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const endDate = new Date(bookingEndDate)
    endDate.setHours(0, 0, 0, 0)
    const diff = Math.ceil(
      (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    )
    if (diff < 0) return null
    if (diff === 0) return 'Last day today'
    if (diff === 1) return '1 day left'
    return `${diff} days left`
  }

  if (isLoading)
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
        <p className="text-gray-500 text-sm font-medium">
          Loading upcoming programs...
        </p>
      </div>
    )

  if (isError)
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <p className="text-red-500 font-medium">
          Error loading upcoming programs
        </p>
      </div>
    )

  if (programs.length === 0)
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <span className="text-3xl">📅</span>
        </div>
        <p className="text-gray-500 font-medium">No upcoming programs found</p>
      </div>
    )

  return (
    <div className="py-6 relative">
      {/* Left Arrow */}
      <button
        onClick={scrollLeft}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow-[0_4px_16px_rgba(0,0,0,0.15)] border border-gray-100 flex items-center justify-center text-gray-700 hover:bg-orange-500 hover:text-white hover:shadow-[0_6px_20px_rgba(249,115,22,0.4)] transition-all duration-300 opacity-0 cursor-pointer opacity-100"
        aria-label="Scroll left"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      {/* Horizontal track */}
      <div className="overflow-hidden mx-12">
        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {programs.map((event: any, index: number) => {
            const status = event.status || 'Upcoming'
            const daysLeft = getDaysLeft(event.booking_end_date)
            const bookingOpen = isBookingOpen(event.booking_end_date)
            const roles =
              (event.role_id || [])
                .map((roleId: number) => ROLE_DISPLAY_NAME[roleId])
                .filter(Boolean) || []

            return (
              <div
                key={event.id || index}
                data-card
                className="snap-start shrink-0 w-[calc(50%-12px)] sm:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] group relative bg-white rounded-3xl overflow-hidden flex flex-col shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_8px_rgba(0,0,0,0.06),0_16px_40px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all duration-400 ease-out border border-gray-50"
              >
                {/* Accent top bar */}
                <div className="h-1 w-full bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500" />

                {/* Image */}
                <div className="relative overflow-hidden h-44">
                  <img
                    src={event.image || event.thumb}
                    alt={event.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

                  {/* Status pill */}
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white/95 text-[#E44342] shadow-sm backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E44342] animate-pulse" />
                    {status}
                  </span>

                  {/* Mode pill */}
                  {event.mode && (
                    <span
                      className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-[11px] font-semibold backdrop-blur-sm ${
                        event.mode?.includes('Online')
                          ? 'bg-white/95 text-orange-600'
                          : 'bg-white/95 text-emerald-600'
                      }`}
                    >
                      {event.mode}
                    </span>
                  )}

                  {/* Days left chip */}
                  {daysLeft && bookingOpen && (
                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur-sm shadow-sm">
                      <span className="text-[11px] font-bold text-orange-600">
                        ⏳ {daysLeft}
                      </span>
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-[16px] font-bold text-gray-900 leading-snug line-clamp-2 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                    {event.name.charAt(0).toUpperCase() + event.name.slice(1)}
                  </h3>

                  {/* Registration date */}
                  {event.booking_end_date && (
                    <div className="inline-flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-blue-50/70 border border-blue-100">
                      <span className="text-blue-500 text-sm">📅</span>
                      <div className="leading-tight">
                        <p className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider">
                          Registration Ends
                        </p>
                        <p className="text-[13px] font-bold text-blue-600">
                          {formatDate(event.booking_end_date)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {event.location && (
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-gray-400 text-sm">📍</span>
                      <p className="text-[13px] text-gray-500 font-medium truncate">
                        {event.location}
                      </p>
                    </div>
                  )}

                  {/* Eligible applicants */}
                  {roles.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Eligible Applicants
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {roles.map((roleName: string, idx: number) => (
                          <span
                            key={idx}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-gray-50 text-gray-600 border border-gray-100 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200 transition-colors duration-200"
                          >
                            {roleName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    {/* Date range */}
                    <div className="flex items-center justify-between mb-4 gap-2">
                      {event.date && (
                        <div className="flex-1 text-center py-2 rounded-xl bg-emerald-50 border border-emerald-100">
                          <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-wider">
                            Start
                          </p>
                          <p className="text-[12px] font-bold text-emerald-700 mt-0.5">
                            {formatDate(event.date)}
                          </p>
                        </div>
                      )}
                      {event.date && event.end_date && (
                        <span className="text-gray-300 text-xs font-bold">
                          →
                        </span>
                      )}
                      {event.end_date && (
                        <div className="flex-1 text-center py-2 rounded-xl bg-rose-50 border border-rose-100">
                          <p className="text-[9px] font-bold text-rose-500 uppercase tracking-wider">
                            End
                          </p>
                          <p className="text-[12px] font-bold text-rose-700 mt-0.5">
                            {formatDate(event.end_date)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    {bookingOpen ? (
                      <button
                        onClick={() =>
                          navigate(
                            `/departments/learning/program-details/${event.id}`
                          )
                        }
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white bg-gradient-to-r from-[#F97316] to-[#FBBF24] hover:from-[#EA580C] hover:to-[#F59E0B] shadow-[0_4px_14px_rgba(249,115,22,0.35)] hover:shadow-[0_6px_20px_rgba(249,115,22,0.45)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                      >
                        Apply Now
                        <span className="transition-transform duration-300 group-hover:translate-x-0.5">
                          →
                        </span>
                      </button>
                    ) : (
                      <div className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-[13px] font-semibold text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed">
                        Enrollment Closed
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {/* Right Arrow */}
      <button
        onClick={scrollRight}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow-[0_4px_16px_rgba(0,0,0,0.15)] border border-gray-100 flex items-center justify-center text-gray-700 hover:bg-orange-500 hover:text-white hover:shadow-[0_6px_20px_rgba(249,115,22,0.4)] transition-all duration-300 opacity-0 cursor-pointer opacity-100"
        aria-label="Scroll right"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>
  )
}

export default UpcomingProgramList
